import React from 'react'
import { Form } from 'react-bootstrap'
import { Organization } from 'interfaces/organization'
import { User } from 'interfaces/user'
import { SaveButton } from './save-button'

interface OrganizationTableRowProps {
  org: Organization
  internalAdmins: User[]
  selectedAssignments: Record<
    string,
    { account_manager_user_id?: string; sale_rep_id?: string }
  >
  handleAssignmentChange: (
    orgId: string,
    field: 'account_manager_user_id' | 'sale_rep_id',
    value: string
  ) => void
  getSelectedId: (
    orgId: string,
    field: 'account_manager_user_id' | 'sale_rep_id',
    fallback: string | number | null | undefined
  ) => string
  isValidId: (id: unknown) => boolean
  getEntityType: (url: string) => string
  isUpdatedPage?: boolean
}

const OrganizationTableRow: React.FC<OrganizationTableRowProps> = ({
  org,
  internalAdmins,
  selectedAssignments,
  handleAssignmentChange,
  getSelectedId,
  isValidId,
  getEntityType,
  isUpdatedPage = false,
}) => {
  const accountManagerValue = getSelectedId(
    org.id,
    'account_manager_user_id',
    org.account_manager_user_id
  )
  const saleRepValue = getSelectedId(
    org.id,
    'sale_rep_id',
    org.organization_subscription?.sale_rep_id
  )

  return (
    <tr key={org.id}>
      <td style={{ width: '200px', paddingLeft: '15px' }}>
        <span style={{ fontSize: '13px' }}>
          <strong>{org.name}</strong>
        </span>
      </td>
      <td style={{ width: '80px' }}>
        <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
          {org.id}
        </span>
      </td>
      <td style={{ width: '80px' }}>
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {org.organization_subscription?.seat_count || org.seat_count || 'N/A'}
        </span>
      </td>
      <td style={{ width: '80px' }}>
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {(org.organization_subscription as any)?.recurring_amount &&
          (org.organization_subscription as any).recurring_amount !== null ? (
            `$${(
              (org.organization_subscription as any).recurring_amount / 100
            ).toFixed(2)}`
          ) : (org.organization_subscription as any)?.amount &&
            (org.organization_subscription as any).amount !== null ? (
            `$${((org.organization_subscription as any).amount / 100).toFixed(
              2
            )}`
          ) : org.subscription_amount ? (
            `$${(parseFloat(org.subscription_amount) / 100).toFixed(2)}`
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </span>
      </td>
      <td>
        {org.organization_subscription?.subscription_id ? (
          <a
            href={`https://dashboard.stripe.com/subscriptions/${org.organization_subscription.subscription_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary btn-sm"
            style={{
              fontSize: '12px',
              padding: '2px 6px',
              color: 'white !important',
              textDecoration: 'none',
            }}
          >
            <span style={{ color: 'white' }}>Stripe</span>
          </a>
        ) : org.subscription_id ? (
          <a
            href={`https://dashboard.stripe.com/subscriptions/${org.subscription_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary btn-sm"
            style={{
              fontSize: '12px',
              padding: '2px 6px',
              color: 'white !important',
              textDecoration: 'none',
            }}
          >
            <span style={{ color: 'white' }}>Stripe</span>
          </a>
        ) : (
          'N/A'
        )}
      </td>
      <td>
        {org.internal_crm_url ? (
          <a
            href={org.internal_crm_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary btn-sm"
            style={{
              fontSize: '12px',
              padding: '2px 6px',
              color: 'white !important',
              textDecoration: 'none',
            }}
          >
            <span style={{ color: 'white' }}>
              {getEntityType(org.internal_crm_url)}
            </span>
          </a>
        ) : (
          'N/A'
        )}
      </td>
      <td>
        <Form.Control
          as="select"
          size="sm"
          value={accountManagerValue}
          onChange={(e) =>
            handleAssignmentChange(
              org.id,
              'account_manager_user_id',
              e.target.value
            )
          }
          style={{
            backgroundColor:
              accountManagerValue === ''
                ? 'white'
                : isValidId(accountManagerValue)
                ? '#d4edda'
                : 'white',
            borderColor:
              accountManagerValue === ''
                ? '#ced4da'
                : isValidId(accountManagerValue)
                ? '#c3e6cb'
                : '#ced4da',
          }}
        >
          <option value="">Select Account Manager</option>
          {internalAdmins?.map((admin: User) => (
            <option key={admin.id} value={admin.id}>
              {admin.name || admin.first_name || admin.email}
            </option>
          ))}
        </Form.Control>
      </td>
      <td>
        <Form.Control
          as="select"
          size="sm"
          value={saleRepValue}
          onChange={(e) =>
            handleAssignmentChange(org.id, 'sale_rep_id', e.target.value)
          }
          style={{
            backgroundColor:
              saleRepValue === ''
                ? 'white'
                : isValidId(saleRepValue)
                ? '#d4edda'
                : 'white',
            borderColor:
              saleRepValue === ''
                ? '#ced4da'
                : isValidId(saleRepValue)
                ? '#c3e6cb'
                : '#ced4da',
          }}
        >
          <option value="">Select Sales Rep</option>
          {internalAdmins?.map((admin: User) => (
            <option key={admin.id} value={admin.id}>
              {admin.name || admin.first_name || admin.email}
            </option>
          ))}
        </Form.Control>
      </td>
      <td style={{ width: '120px' }}>
        <span
          style={{
            fontSize: '11px',
            color: '#bdc3c7',
            fontFamily: 'monospace',
          }}
        >
          {org.created_at
            ? new Date(org.created_at).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: '2-digit',
              })
            : 'N/A'}
        </span>
      </td>
      <td>
        <SaveButton
          org={org}
          selectedAssignments={selectedAssignments}
          internalAdmins={internalAdmins}
          isUpdatedPage={isUpdatedPage}
        />
      </td>
    </tr>
  )
}

export default OrganizationTableRow
